import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { makeApiUrl } from '../../utils/reactQuery';
import apiRoutes from '../../setup/consts/apiRoutes';
import {
  CreateReportParams,
  GetReportsResponse,
  ReportManifest,
} from './types';

export const useReports = () => {
  const { data, isLoading, isError } = useQuery<
    UseQueryResponse<GetReportsResponse>
  >(apiRoutes.CLIENT_REPORTS, {
    refetchOnWindowFocus: false,
    // Reports generate in the background; poll while any is still "processing"
    // so it flips to Ready/Failed on its own, then stop polling.
    refetchInterval: (current) =>
      (current?.response?.reports ?? []).some((r) => r.status === 'processing')
        ? 4000
        : false,
  });

  return {
    reports: data?.response.reports ?? [],
    isLoading,
    isError,
  };
};

export const useCreateReport = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useMutation(
    async (params: CreateReportParams): Promise<ReportManifest> => {
      const { data } = await axios.post<ReportManifest>(
        makeApiUrl(apiRoutes.CLIENT_REPORTS),
        params
      );
      return data;
    },
    { onSuccess: () => queryClient.invalidateQueries(apiRoutes.CLIENT_REPORTS) }
  );
  return { createReport: mutateAsync, isCreating: isLoading };
};

export const useDownloadReport = () => {
  const { mutateAsync, isLoading } = useMutation(
    async (reportId: string): Promise<string> => {
      const { data } = await axios.get<{ url: string }>(
        makeApiUrl(apiRoutes.CLIENT_REPORT_DOWNLOAD(reportId))
      );
      return data.url;
    }
  );
  return { getDownloadUrl: mutateAsync, isResolving: isLoading };
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading } = useMutation(
    async (reportId: string): Promise<void> => {
      await axios.delete(makeApiUrl(apiRoutes.CLIENT_REPORT_DELETE(reportId)));
    },
    { onSuccess: () => queryClient.invalidateQueries(apiRoutes.CLIENT_REPORTS) }
  );
  return { deleteReport: mutateAsync, isDeleting: isLoading };
};
